<template>
    <TItems
        :footer-button="footerButtonTheme"
        :active-buttons="activeIndexButtons"
        :footer-button-text="footerButtonText"
        :current-letter-index-panel="currentLetterIndexPanel"
        class="o-d-category d-category"
        index-panel
        @clickFooterButton="handleFooterClick"
        @clickLetter="handleLetterClick"
        :is-footer="false"
        :is-icon-down="false"
    >
        <template #header>
            <ODMenuHeader
                :title="categoryName"
                :subtitle="categoryDescription"
            >
                <template #rightIcon>
                    <div class="o-d-category__header-icons">
                        <svg-icon
                            original
                            name="icons/m-search-setting"
                            class="o-d-category__header-icons__setting"
                        />
                        <img
                            v-if="categoryIcon"
                            :src="categoryIcon"
                            alt="logo image"
                            class="o-d-category__header-icons__logo"
                        />
                        <svg-icon
                            v-else
                            original
                            name="icons/search_mobile"
                            class="o-d-category__header-icons__search"
                        />
                    </div>
                </template>
            </ODMenuHeader>
            <div 
                class="o-d-category__header-line"
                :style="`background-color: ${categoryColor}`"
            />
        </template>
        <template #main>
            <RecycleScroller
                ref="tilesList"
                #default="{ item, index }"
                :items="filteredItems"
                :item-size="67"
                class="o-d-category__scroller"
                emitUpdate
                @update="highlightActiveLetter"
            >
                <OWItemSearchTile
                    v-if="t(item, 'type') === 'tenant' || t(item, 'type') === 'service'"
                    :item="item"
                    @onClick="handleTileClick"
                    :style="`background-color: ${index%2 == 0|| index == 0 ? colorConvert(categoryColorCode) : '#fff'};`"
                />
                <OWItemSearchCategoryTile
                    v-else
                    :item="item"
                    :style="`background-color: ${index%2 == 0|| index == 0 ? colorConvert(categoryColorCode) : '#fff'};`"
                    @onClick="handleTileClick"
                />
            </RecycleScroller>
        </template>
    </TItems>
</template>

<script>
import uniq from "lodash/uniq";

import { mapGetters } from 'vuex';

import TItems from '../../templates/t-items/t-items';
import ODMenuHeader from '../../organisms/o-d-menu-header/o-d-menu-header';
import OWItemSearchTile from '../../organisms/o-w-item-search-tile/o-w-item-search-tile';
import OWItemSearchCategoryTile from '../../organisms/o-w-item-search-category-tile/o-w-item-search-category-tile';

import webItemFunctionality from '../../../utils/mixins/web-items-functionality';
import firstElementTabNavigation from '../../../utils/mixins/first-element-tab-navigations';

import {colorConvert} from '../../../utils/helpers/colorConvert';

export default {
    name: 'ODcategory',
    components: {
        TItems,
        ODMenuHeader,
        OWItemSearchTile,
        OWItemSearchCategoryTile
    },
    data() {
        return {
            filteredByLetter: null,
            currentLetterIndexPanel: 'a',
            tabNavigationStartElement: '.o-w-item-tile',
            scrollerItemsNumber: 0 // This variable shows us how many items be in RecycleScroller
        }
    },
    mixins: [
        webItemFunctionality,
        firstElementTabNavigation
    ],
    props: {
        guide: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters([
            't',
            'getListItem',
            'getTenantsAZ',
            'getMapConfigProp',
            'getObjectsByCategoryAZ',
            'getDenormalisedObjectById'
        ]),
        filteredItems() {
            const t = this.t;
            const tenants = this.categoryIdInt ?
                this.getObjectsByCategoryAZ(this.categoryIdInt) :
                this.getTenantsAZ;

            const filteredTenantsByItem = this.filteredByItem ? tenants.filter(item => item.id !== this.filteredByItem) : tenants;

            if (this.sortingValue && this.sortingValue !== 'All') {
                const valueLength = this.sortingValue.length;
                const selectedItems = [];
                for (let i = 0; i < valueLength; i++) {
                    selectedItems.push(...filteredTenantsByItem.filter((name) => {
                        const firstLetter = t(name, "name").charAt(0);
                        return firstLetter === this.sortingValue.charAt(i);
                    }));
                }
                return selectedItems;
            }
            return tenants;
        },
        activeIndexButtons() {
            return uniq(this.getTenantsAZ.map((item) => {
                return this.t(item, "name").charAt(0).toLowerCase();
            }));
        },
        categoryIdInt(){
            return this.$route.params.categoryId ? parseInt(this.$route.params.categoryId) : null;
        },
        category(){
            return this.categoryIdInt ? this.getListItem(this.categoryIdInt) : null;
        },
        categoryColor(){
            return this.t(this.category, "var_color") ? `var(${this.t(this.category, "var_color")})` : this.t(this.category, "color")
        },
        categoryColorCode() {
            return this.t(this.category, "var_color") ? this.getMapConfigProp(`ui_colors.${this.t(this.category, "var_color")}`, null) : this.t(this.category, "color")
        },
        categoryName(){
            return this.t(this.category, "name")
        },
        categoryDescription(){
            return this.t(this.category, "description")
        },
        categoryIcon(){
            return this.t(this.category, "menuIcon")
        }
    },
    mounted() {
        this.scrollerItemsNumber = Math.ceil( document.querySelector('.o-d-category__scroller').clientHeight / 67) + 5;
        this.currentLetterIndexPanel = this.t(this.filteredItems[0], 'name').charAt(0).toLowerCase();
    },
    methods: {
        handleTileClick(data) {
            if (this.guide) {
                this.$emit('onTileClick', data);
            } else {
                const params = {
                    name: 'web.guide',
                    params: {
                        type: 'category',
                        categoryId: this.categoryIdInt
                    },
                    query: {
                        to: data.id
                    }
                };
                this.$router.push(params);
            }
        },
        highlightActiveLetter(start, end) {
            const item = this.filteredItems[start + 3];
            if(end < this.filteredItems.length && end > this.scrollerItemsNumber) 
                this.currentLetterIndexPanel = this.t(item, 'name').charAt(0).toLowerCase();
        },
        handleLetterClick(letter) {
            const itemIndex = this.filteredItems.findIndex( i  => this.t(i,'name',' ').charAt(0).toLowerCase() === letter);
            if(itemIndex !== -1)
                this.$refs.tilesList.scrollToItem(itemIndex)
        },
        colorConvert
    }
}
</script>

<style lang="scss">
@import "o-d-category";
</style>
